import React, { useEffect, useMemo } from "react";
import GlobalStoreContext from "../../../context/GlobalStoreContext";
import { CalendarEvent } from "../../../models/CalendarEvent";
import { Dropoff } from "../../../models/Dropoff";
import { Gate } from "../../../models/Gate";
import { HaulRoute } from "../../../models/HaulRoute";
import { MapObject } from "../../../models/MapObject";
import PlotAPI from "../../../plotAPI";
import { gateToDropoffRoutesForPair } from "../../Helper/Map";
import { ALTERNATE_HAUL_ROUTE_COLOR } from "./haulRouteSelector";

// Everything a delivery's DAMN map shows, worked out once so the page's map and its PDF export draw the same thing.
export const useDamnMapItems = (calendarEvent: CalendarEvent) => {
    const context = React.useContext(GlobalStoreContext);

    const {
        dropoffsByProject,
        dropoffsForProject,
        mapObjectsByProject,
        haulRoutesForProject,
        haulRoutesByProject,
        gatesForProject,
        gateToDropoffRoutesForProject,
        gateToDropoffRoutesByProject,
        projectInfo,
    } = context;

    const project = projectInfo(calendarEvent.projectId);

    // Worked out during render rather than in an effect, so the items are complete on the first render. The PDF export
    // decides whether there's a map to draw when it opens, from whatever items it has at that moment.
    const dropoff = useMemo<Dropoff | null>(() => {
        if (!project) return null;
        return (
            dropoffsForProject(project.id).find((dropoff) => dropoff.id === calendarEvent.deliveryDropOffId()) || null
        );
    }, [calendarEvent, dropoffsByProject, dropoffsForProject, project]);
    // Once a haul route has been assigned to a delivery it should keep showing here even if the
    // haul route is later archived (e.g. a completed delivery's historical record shouldn't look
    // like its haul route reference disappeared). haulRoutesForProject only returns active routes,
    // so an archived-but-assigned route is fetched separately as a fallback.
    const [archivedAssignedHaulRoute, setArchivedAssignedHaulRoute] = React.useState<HaulRoute | null>(null);

    const selectedHaulRouteId = calendarEvent.deliveryHaulRouteId();
    const gateId = calendarEvent.deliveryGateId();

    const dropOffMapObject = useMemo(() => {
        if (!dropoff) return null;
        return dropoff.getMapObject(context);
    }, [context, dropoff, mapObjectsByProject]);

    const gate = useMemo(() => {
        if (!project) return null;
        return gatesForProject(project.id)?.find((gate: Gate) => gate.id === gateId) || null;
    }, [gatesForProject, project, gateId]);

    const gateMapObject = useMemo(() => {
        if (!gate) return null;
        return gate.getMapObject(context);
    }, [context, gate, mapObjectsByProject]);

    // The leg from the gate to the dropoff once inside the jobsite, which follows from the delivery's gate and dropoff.
    const gateToDropoffRoutes = useMemo(() => {
        if (!project) return [];
        return gateToDropoffRoutesForPair(
            gateToDropoffRoutesForProject(project.id),
            gate?.id ?? null,
            dropoff?.id ?? null
        );
    }, [gateToDropoffRoutesForProject, gateToDropoffRoutesByProject, project, gate, dropoff]);

    const gateToDropoffRouteMapObjects = useMemo(
        () =>
            gateToDropoffRoutes
                .map((route) => route.getMapObject(context))
                .filter((mapObject): mapObject is MapObject => mapObject !== null),
        [context, gateToDropoffRoutes, mapObjectsByProject]
    );

    const haulRoutes = useMemo(() => {
        if (!project) return [];
        return haulRoutesForProject(project.id);
    }, [haulRoutesForProject, haulRoutesByProject, project]);

    const selectedHaulRoute = useMemo(() => {
        return (
            haulRoutes.find((haulRoute) => haulRoute.id === selectedHaulRouteId) ||
            (archivedAssignedHaulRoute?.id === selectedHaulRouteId ? archivedAssignedHaulRoute : null)
        );
    }, [haulRoutes, selectedHaulRouteId, archivedAssignedHaulRoute]);

    const selectedHaulRouteMapObject = useMemo(() => {
        if (!selectedHaulRoute) return null;
        return selectedHaulRoute.getMapObject(context);
    }, [context, selectedHaulRoute]);

    // Until a route is picked every option shows in its own color so they can be told apart; once one is
    // picked the alternates go grey and the pick is rendered last so it sits on top, like a Google Maps
    // route picker. The pick comes from selectedHaulRouteMapObject (not the active-only haulRoutes list)
    // so an archived-but-assigned route still draws on the map.
    const haulRouteMapObjects = useMemo(() => {
        const alternates: MapObject[] = haulRoutes
            .filter((haulRoute) => haulRoute.id !== selectedHaulRouteId)
            .map((haulRoute) => haulRoute.getMapObject(context))
            .filter((mapObject): mapObject is MapObject => mapObject !== null);

        if (!selectedHaulRouteMapObject) {
            return alternates;
        }

        const greyedAlternates = alternates.map((mapObject) => {
            const displayMapObject = Object.create(mapObject) as MapObject;
            displayMapObject.color = ALTERNATE_HAUL_ROUTE_COLOR;
            displayMapObject.outline = ALTERNATE_HAUL_ROUTE_COLOR;
            return displayMapObject;
        });

        return [...greyedAlternates, selectedHaulRouteMapObject];
    }, [context, haulRoutes, selectedHaulRouteId, selectedHaulRouteMapObject]);

    const mapObjects = useMemo(() => {
        return [dropOffMapObject, gateMapObject, ...haulRouteMapObjects, ...gateToDropoffRouteMapObjects].filter(
            (obj): obj is MapObject => obj !== null
        );
    }, [dropOffMapObject, gateMapObject, haulRouteMapObjects, gateToDropoffRouteMapObjects]);

    useEffect(() => {
        if (!project || !selectedHaulRouteId) {
            setArchivedAssignedHaulRoute(null);
            return;
        }

        if (haulRoutes.some((haulRoute) => haulRoute.id === selectedHaulRouteId)) {
            setArchivedAssignedHaulRoute(null);
            return;
        }

        if (archivedAssignedHaulRoute?.id === selectedHaulRouteId) {
            return;
        }

        PlotAPI.getProjectHaulRoute(
            project.claimed_by_team_subdomain || "projects",
            project.unique_token,
            selectedHaulRouteId,
            project.timezone
        ).then((haulRoute) => setArchivedAssignedHaulRoute(haulRoute));
    }, [project, selectedHaulRouteId, haulRoutes, archivedAssignedHaulRoute]);

    return {
        project,
        dropoff,
        dropOffMapObject,
        gate,
        gateMapObject,
        gateToDropoffRoutes,
        haulRoutes,
        selectedHaulRouteId,
        selectedHaulRoute,
        selectedHaulRouteMapObject,
        mapObjects,
    };
};

export type DamnMapItems = ReturnType<typeof useDamnMapItems>;
